import type {
    ComponentStageData,
    ConversionDal,
    InnerElementStageData,
    MobileAlgoPlugin,
    MobileAlgoPluginInitializationArgs,
    PluginHeuristicsRegistry
} from '../../types'
import type {Pointer} from '@wix/document-services-types'
import type {SnapshotDal} from '@wix/document-manager-core'
import {getSourceLayoutPointer, getTargetLayoutPointer, getStylePointer} from '../../conversionDal/utils/pointer'
import {createAdaptiveHeuristic} from '../common'

export const createPlugin = ({stages}: MobileAlgoPluginInitializationArgs): MobileAlgoPlugin => {
    const initializeTargetLayout = ({conversionDal, componentPointer, config}: ComponentStageData) => {
        const targetLayoutPointer = getTargetLayoutPointer(componentPointer)
        const sourceLayout = conversionDal.get(getSourceLayoutPointer(componentPointer))

        conversionDal.set(targetLayoutPointer, {
            componentLayout: {
                hidden: false,
                type: 'ComponentLayout'
            },
            itemLayout: {},
            containerLayout: {},
            variableConnections: sourceLayout.variableConnections,
            spx: {
                refWidth: config.targetCanvasSize,
                resolverType: 'scale'
            },
            type: 'SingleLayoutData'
        })
    }

    const createStyleInitializer =
        <T extends {conversionDal: ConversionDal; initialSnapshot: SnapshotDal; config: any}>(
            getPointer: (stageData: T) => Pointer
        ) =>
        (stageData: T) => {
            const stylePointer = getStylePointer(getPointer(stageData))
            const initialStyle = stageData.initialSnapshot.getValue(stylePointer)

            if (initialStyle) {
                stageData.conversionDal.set(stylePointer, {
                    cssProperties: {},
                    cssCustomProperties: {},
                    spx: {
                        resolverType: 'scale',
                        refWidth: stageData.config.targetCanvasSize
                    },
                    type: initialStyle.type
                })
            }
        }

    const initializeComponentStyle = createStyleInitializer<ComponentStageData>(stageData => stageData.componentPointer)

    const initializeInnerElementStyle = createStyleInitializer<InnerElementStageData>(
        stageData => stageData.innerElementPointer
    )

    const register = (registry: PluginHeuristicsRegistry) => {
        registry.register(stages.INIT_CSS_MODELS, createAdaptiveHeuristic(initializeTargetLayout))
        registry.register(stages.INIT_CSS_MODELS, createAdaptiveHeuristic(initializeComponentStyle))
        registry.registerInnerElementHandler(
            stages.INIT_CSS_MODELS,
            createAdaptiveHeuristic(initializeInnerElementStyle)
        )
    }

    const name = 'initializeCssModels'
    return {register, name}
}
